import React, {useEffect, useState} from 'react';
import {Button, Card, CardBody, CardHeader, Typography} from "@material-tailwind/react";
import {NumericFormat} from "react-number-format";
import MapComponent from "../../shared/map_component";
import {format} from "date-fns";
import {es} from "date-fns/locale";
import {PrinterIcon} from "@heroicons/react/24/solid";
import axios from "axios";

const LotDetails = ({parcelCode, lot, movement}) => {
    const [debts, setDebts] = useState([]);
    const [constructions, setConstructions] = useState([]);
    const [lands, setLands] = useState(null);
    const [lastPayment, setLastPayment] = useState(null);

    const getCSRFToken = () => {
        const token = document.querySelector('meta[name="csrf-token"]');
        return token ? token.content : "";
    };

    useEffect(() => {
        async function fetchLotDetails() {
            try {
                const [debtsResponse, constructionsResponse, landResponse, paymentResponse] = await Promise.all([
                    fetch(`/lots/${parcelCode}/yearly_debts.json`),
                    fetch(`/lots/${parcelCode}/constructions.json`),
                    fetch(`/lots/${parcelCode}/land.json`),
                    fetch(`/lots/${parcelCode}/last_payment.json`),
                ]);

                if (debtsResponse.ok) {
                    const debtsData = await debtsResponse.json();
                    setDebts(debtsData);
                } else {
                    console.log("No se encontraron adeudos para este código.");
                }

                if (constructionsResponse.ok) {
                    const constructionsData = await constructionsResponse.json();
                    setConstructions(constructionsData);
                } else {
                    console.log("No se encontraron construcciones para este código.");
                }

                if (landResponse.ok) {
                    const landData = await landResponse.json();
                    setLands(landData);

                } else {
                    console.log("No se encontraron terrenos para este código.");
                }

                if (paymentResponse.ok) {
                    const lastPaymentData = await paymentResponse.json();
                    setLastPayment(lastPaymentData);
                } else {
                    console.log("No se encontraron terrenos para este código.");
                }

            } catch (err) {
                console.error("Error al buscar el predio:", err);
            }
        }

        async function updateMovement() {
            try {
                const response = await axios.patch(`lots/${parcelCode}.json`, {
                    reason_type_id: movement.id,
                }, {
                    headers: {
                        'Content-Type': 'application/json',
                        "X-CSRF-Token": getCSRFToken(),
                    }
                });
                if (response.status === 200) {
                    const url = `/lots/${parcelCode}/print_movement.pdf`;
                    window.open(url, '_blank');
                } else {
                    setError('Error al actualizar contribuyente. Verifica los datos.');
                }

            } catch (err) {
                console.error("Error al actualizar movimiento:", err);
            }
        }

        fetchLotDetails();
        updateMovement();
    }, []);

    const totalDebt = debts.reduce((acc, debt) => acc + debt.total, 0);
    const totalConstArea = constructions.reduce((acc, construction) => acc + construction.area, 0);
    const totalConstValue = constructions.reduce((acc, construction) => acc + construction.value, 0);

    const totalLandArea = lands ? lands.reduce((acc, land) => acc + land.area, 0) : 0;
    const totalLandValue = lands ? lands.reduce((acc, land) => acc + land.value, 0) : 0;

    const totalLandValuation = lands ? lands.reduce((acc, land) => acc + land.valuation, 0) : 0;
    const totalConstValuation = constructions.reduce((acc, construction) => acc + construction.valuation, 0);
    const getUnit = lot.lot_location_type === 'URBANO' ? <>m<sup>2</sup></> : <>ha</>;

    return (
        <>
            {/* Información del predio */}
            <div className="flex-auto pb-2 pt-0">
                <h6 className="font-semibold uppercase text-gray-600 mt-8">
                    Información del predio
                </h6>
                <div className="flex flex-col lg:flex-row gap-4 min-h-[350px]">
                    <div className="flex-grow border p-4 rounded-md w-full lg:w-1/2 overflow-x-hidden">
                        {lot && (
                            <>
                                <table className="w-full table-auto text-left">
                                    <tbody>
                                    <tr>
                                        <td>
                                            <Typography
                                                variant="small"
                                                color="blue-gray"
                                                className="font-bold"
                                            >
                                                Tipo:
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                variant="small"
                                                className="font-normal text-gray-600"
                                            >
                                                {lot.lot_location_type}
                                            </Typography>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <Typography
                                                variant="small"
                                                color="blue-gray"
                                                className="font-bold"
                                            >
                                                Dirección:
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                variant="small"
                                                className="font-normal text-gray-600"
                                            >
                                                {lot.road_name}{lot.ext_num ? " #" + lot.ext_num : ""}, {lot.neighborhood_name},
                                                C.P. {lot.postal_code}
                                            </Typography>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <Typography
                                                variant="small"
                                                color="blue-gray"
                                                className="font-bold"
                                            >
                                                Referencias:
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                variant="small"
                                                className="font-normal text-gray-600"
                                            >
                                                {lot.address_references === null ? '' : lot.address_references}
                                            </Typography>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <Typography
                                                variant="small"
                                                color="blue-gray"
                                                className="font-bold"
                                            >
                                                Sup. Terreno:
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                variant="small"
                                                className="font-normal text-gray-600"
                                            >
                                                {lot.land_area.toFixed(2)} {getUnit}
                                            </Typography>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <Typography
                                                variant="small"
                                                color="blue-gray"
                                                className="font-bold"
                                            >
                                                Sup. Const:
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                variant="small"
                                                className="font-normal text-gray-600"
                                            >
                                                {lot.construction_area.toFixed(2)} m<sup>2</sup>
                                            </Typography>
                                        </td>
                                    </tr>
                                    <tr className='border-t'>
                                        <td className='pt-2'>
                                            <Typography
                                                variant="small"
                                                color="blue-gray"
                                                className="font-bold"
                                            >
                                                Propietario:
                                            </Typography>
                                        </td>
                                        <td className='pt-2'>
                                            <Typography
                                                variant="small"
                                                className="font-normal text-gray-600"
                                            >
                                                {lot.name} {lot.parental_last_name} {lot.maternal_last_name}
                                            </Typography>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <Typography
                                                variant="small"
                                                color="blue-gray"
                                                className="font-bold"
                                            >
                                                Dirección:
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                variant="small"
                                                className="font-normal text-gray-600"
                                            >
                                                {lot.contributor_street}
                                                {lot.contributor_ext_num ? " #" + lot.contributor_ext_num : ""}
                                                {lot.contributor_neighborhood ? ', ' + lot.contributor_neighborhood : ''}
                                                {lot.contributor_postal_code ? ', C.P. ' + lot.contributor_postal_code : ''}
                                            </Typography>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <Typography
                                                variant="small"
                                                color="blue-gray"
                                                className="font-bold"
                                            >
                                                Teléfono:
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                variant="small"
                                                className="font-normal text-gray-600"
                                            >
                                                {lot.phone}
                                            </Typography>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <Typography
                                                variant="small"
                                                color="blue-gray"
                                                className="font-bold"
                                            >
                                                RFC:
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                variant="small"
                                                className="font-normal text-gray-600"
                                            >
                                                {lot.rfc}
                                            </Typography>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <Typography
                                                variant="small"
                                                color="blue-gray"
                                                className="font-bold"
                                            >
                                                Tipo de contribuyente:
                                            </Typography>
                                        </td>
                                        <td>
                                            <Typography
                                                variant="small"
                                                className="font-normal text-gray-600"
                                            >
                                                {lot.contributor_type === 'P' ? 'Persona física' : 'Persona moral'}
                                            </Typography>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                    <div className="flex-grow border rounded-md w-full lg:w-1/2 min-h-[350px]">
                        {lot.latitude && lot.longitude ? (
                            <MapComponent
                                parcel_code={parcelCode}
                                latitude={lot.latitude}
                                longitude={lot.longitude}
                            />
                        ) : (
                            <MapComponent/>
                        )}
                    </div>
                </div>
                <div className="flex grid-cols-3 w-full mt-4 gap-4">
                    <Card className='w-full border' shadow={false}>
                        <CardHeader shadow={false} className='my-2'>
                            <Typography
                                variant="small"
                                color="gray"
                                className="font-medium text-center"
                            >
                                Valores
                            </Typography>
                        </CardHeader>
                        <CardBody className='pt-4'>
                            <div className='flex flex-row gap-4 w-full text-center'>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Calificación
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        <NumericFormat
                                            value={lot.qualification.toFixed(2)}
                                            displayType={'text'}
                                            prefix={'$'}
                                            allowLeadingZeros
                                            thousandSeparator=","
                                        />
                                    </Typography>
                                </div>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Tasa
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lot.rate.toFixed(2)}
                                    </Typography>
                                </div>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Impuesto
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        <NumericFormat
                                            value={lot.tax.toFixed(2)}
                                            displayType={'text'}
                                            prefix={'$'}
                                            allowLeadingZeros
                                            thousandSeparator=","
                                        />
                                    </Typography>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </div>
                <div className="flex grid-cols-3 w-full mt-6 gap-4">
                    <Card className='w-full border' shadow={false}>
                        <CardHeader shadow={false} className='my-2'>
                            <Typography
                                variant="small"
                                color="gray"
                                className="font-medium text-center"
                            >
                                R.P.P.
                            </Typography>
                        </CardHeader>
                        <CardBody className='pt-4'>
                            <div className='flex flex-row gap-4 w-full text-center'>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Insc.
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lot.inscription}
                                    </Typography>
                                </div>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Folio
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lot.folio}
                                    </Typography>
                                </div>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Libro
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lot.book}
                                    </Typography>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                    <Card className='w-full border' shadow={false}>
                        <CardHeader shadow={false} className='my-2'>
                            <Typography
                                variant="small"
                                color="gray"
                                className="font-medium text-center"
                            >
                                Fecha de efecto
                            </Typography>
                        </CardHeader>
                        <CardBody className='pt-4'>
                            <div className='flex flex-row gap-4 w-full text-center'>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Bim.
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lot.effective_bimester}
                                    </Typography>
                                </div>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Año
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lot.effective_year}
                                    </Typography>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                    <Card className='w-full border' shadow={false}>
                        <CardHeader shadow={false} className='my-2'>
                            <Typography
                                variant="small"
                                color="gray"
                                className="font-medium text-center"
                            >
                                Exento
                            </Typography>
                        </CardHeader>
                        <CardBody className='pt-4'>
                            <div className='flex flex-row gap-4 w-full text-center'>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Inicio
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        Bim {lot.start_exempt_bimester} - Año {lot.start_exempt_year}
                                    </Typography>
                                </div>
                                <div className='flex flex-col w-full'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Fin
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        Bim {lot.end_exempt_bimester} - Año {lot.end_exempt_year}
                                    </Typography>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            </div>

            {/* Datos fiscales */}
            <div className="flex-auto pb-2 pt-0">
                <h6 className="font-semibold uppercase text-gray-600 mt-8">
                    Datos Fiscales
                </h6>
                <div className="flex flex-col gap-4">
                    <Card className='w-full border border-blue-gray-50' shadow={false}>
                        <CardBody>
                            <div className='flex flex-row gap-4 w-full text-center mb-6'>
                                <div className='flex flex-col w-full p-4 rounded-md border bg-gray-50'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Último bimestre y año pagado
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lastPayment && (
                                            <>
                                                {lastPayment.bimester} Bim. del Año {lastPayment.year}
                                            </>
                                        )}
                                    </Typography>
                                </div>
                                <div className='flex flex-col w-full p-4 rounded-md border bg-gray-50'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Último impuesto pagado
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lastPayment && (
                                            <NumericFormat
                                                value={Math.ceil(lastPayment.details_tax.toFixed(2))}
                                                displayType={'text'}
                                                prefix={'$'}
                                                allowLeadingZeros
                                                thousandSeparator=","
                                            />)}
                                    </Typography>
                                </div>
                                <div className='flex flex-col w-full p-4 rounded-md border bg-gray-50'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Fecha último pago
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lastPayment && format(lastPayment.created_at, 'PPP', {locale: es})}
                                    </Typography>
                                </div>
                            </div>
                            <Typography
                                variant="small"
                                className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                            >
                                Adeudos
                            </Typography>
                            <table className="w-full min-w-[640px] table-auto">
                                <thead>
                                <tr className='bg-gray-50'>
                                    {["Año", "Impuesto", "Imp. Univ.", "Recargos", "Total"].map(
                                        (el) => (
                                            <th
                                                key={el}
                                                className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                            >
                                                <Typography
                                                    variant="small"
                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                >
                                                    {el}
                                                </Typography>
                                            </th>
                                        )
                                    )}
                                </tr>
                                </thead>
                                <tbody>
                                {(!debts || debts.length === 0) ? (
                                    <tr className="w-full">
                                        <td colSpan="100%" className="text-center py-3">
                                            <strong>El predio no tiene adeudos.</strong>
                                        </td>
                                    </tr>
                                ) : (debts.map((debt, index) => {
                                        const className = `py-3 px-5 ${
                                            index === debts.length - 1 ? "" : "border-b border-blue-gray-50"
                                        }`;
                                        return (
                                            <tr key={debt.year} className="hover:bg-gray-50 cursor-pointer">
                                                <td className={className}>{debt.year}</td>
                                                <td className={className}>
                                                    <NumericFormat
                                                        value={debt.tax.toFixed(2)}
                                                        displayType={'text'}
                                                        prefix={'$'}
                                                        allowLeadingZeros
                                                        thousandSeparator=","
                                                    />
                                                </td>
                                                <td className={className}>
                                                    <NumericFormat
                                                        value={debt.university_tax.toFixed(2)}
                                                        displayType={'text'}
                                                        prefix={'$'}
                                                        allowLeadingZeros
                                                        thousandSeparator=","
                                                    />
                                                </td>
                                                <td className={className}>
                                                    <NumericFormat
                                                        value={debt.surcharge.toFixed(2)}
                                                        displayType={'text'}
                                                        prefix={'$'}
                                                        allowLeadingZeros
                                                        thousandSeparator=","
                                                    />
                                                </td>
                                                <td className={className}>
                                                    <NumericFormat
                                                        value={debt.total.toFixed(2)}
                                                        displayType={'text'}
                                                        prefix={'$'}
                                                        allowLeadingZeros
                                                        thousandSeparator=","
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                                </tbody>
                                <tfoot>
                                <tr className='border-t'>
                                    <td colSpan='4' className='py-3 px-5 text-right border-r'>
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                        >
                                            Adeudo total
                                        </Typography>
                                    </td>
                                    <td className='py-3 px-5 bg-gray-50 border-b cursor-pointer'>
                                        <NumericFormat
                                            value={totalDebt.toFixed(2)}
                                            displayType={'text'}
                                            prefix={'$'}
                                            allowLeadingZeros
                                            thousandSeparator=","
                                        /></td>
                                </tr>
                                </tfoot>
                            </table>
                        </CardBody>
                    </Card>

                </div>
            </div>

            {/* Base Impuesto */}
            <div className="flex-auto pb-2 pt-0">
                <h6 className="font-semibold uppercase text-gray-600 mt-8">
                    Base del Impuesto
                </h6>
                <div className="flex flex-col gap-4">
                    <div className="flex grid-cols-3 w-full gap-4">
                        <Card className='w-full border border-blue-gray-50' shadow={false}>
                            <CardBody>
                                <div className='flex flex-row gap-4 w-full text-center'>
                                    <div className='flex flex-col w-full'>
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                        >
                                            Valor de Terreno
                                        </Typography>
                                        <Typography
                                            variant="paragraph"
                                            className="font-medium"
                                        >
                                            <NumericFormat
                                                value={lot.land_value.toFixed(2)}
                                                displayType={'text'}
                                                prefix={'$'}
                                                allowLeadingZeros
                                                thousandSeparator=","
                                            />
                                        </Typography>
                                    </div>
                                    <div className='flex flex-col w-full'>
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                        >
                                            Valor de construcción
                                        </Typography>
                                        <Typography
                                            variant="paragraph"
                                            className="font-medium"
                                        >
                                            <NumericFormat
                                                value={lot.construction_value.toFixed(2)}
                                                displayType={'text'}
                                                prefix={'$'}
                                                allowLeadingZeros
                                                thousandSeparator=","
                                            />
                                        </Typography>
                                    </div>
                                    <div className='flex flex-col w-full'>
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                        >
                                            Calificación
                                        </Typography>
                                        <Typography
                                            variant="paragraph"
                                            className="font-medium"
                                        >
                                            <NumericFormat
                                                value={lot.qualification.toFixed(2)}
                                                displayType={'text'}
                                                prefix={'$'}
                                                allowLeadingZeros
                                                thousandSeparator=","
                                            />
                                        </Typography>
                                    </div>
                                    <div className='flex flex-col w-full'>
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                        >
                                            Tasa
                                        </Typography>
                                        <Typography
                                            variant="paragraph"
                                            className="font-medium"
                                        >
                                            {lot.rate}
                                        </Typography>
                                    </div>
                                    <div className='flex flex-col w-full'>
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                        >
                                            Impuesto
                                        </Typography>
                                        <Typography
                                            variant="paragraph"
                                            className="font-medium"
                                        >
                                            <NumericFormat
                                                value={lot.tax.toFixed(2)}
                                                displayType={'text'}
                                                prefix={'$'}
                                                allowLeadingZeros
                                                thousandSeparator=","
                                            />
                                        </Typography>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Terreno */}
            <div className="flex-auto pb-2 pt-0">
                <h6 className="font-semibold uppercase text-gray-600 mt-8">
                    Terreno
                </h6>
                <div className="flex flex-col lg:flex-row gap-4">
                    <div className="flex grid-cols-3 w-full gap-4">
                        <Card className='w-full border border-blue-gray-50' shadow={false}>
                            <CardBody>
                                {lot.lot_location_type === 'URBANO' ? (
                                    <div className='flex flex-row gap-4 w-full text-center'>
                                        <div className='flex flex-col w-full'>
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase text-blue-gray-400"
                                            >
                                                Superficie
                                            </Typography>
                                            <Typography
                                                variant="paragraph"
                                                className="font-medium"
                                            >
                                                <NumericFormat
                                                    value={lot.land_area.toFixed(2)}
                                                    displayType={'text'}
                                                    allowLeadingZeros
                                                    thousandSeparator=","
                                                /> {getUnit}
                                            </Typography>
                                        </div>
                                        <div className='flex flex-col w-full'>
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase text-blue-gray-400"
                                            >
                                                Valor del terreno
                                            </Typography>
                                            <Typography
                                                variant="paragraph"
                                                className="font-medium"
                                            >
                                                <NumericFormat
                                                    value={lot.land_value.toFixed(2)}
                                                    displayType={'text'}
                                                    prefix={'$'}
                                                    allowLeadingZeros
                                                    thousandSeparator=","
                                                />
                                            </Typography>
                                        </div>
                                    </div>
                                ) : (
                                    <table className="w-full min-w-[640px] table-auto border">
                                        <thead>
                                        <tr className='bg-gray-50'>
                                            {["Cve. Val.", "Descripción", "Superficie", "Valor"].map(
                                                (el) => (
                                                    <th
                                                        key={el}
                                                        className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                                    >
                                                        <Typography
                                                            variant="small"
                                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                        >
                                                            {el}
                                                        </Typography>
                                                    </th>
                                                )
                                            )}
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {(!lands || lands.length === 0) ? (
                                            <tr className="w-full">
                                                <td colSpan="100%" className="text-center py-3">
                                                    <strong>El predio no cuenta con terrenos registrados.</strong>
                                                </td>
                                            </tr>
                                        ) : (lands.map((land, index) => {
                                                const className = `py-3 px-5 ${
                                                    index === land.length - 1 ? "" : "border-b border-blue-gray-50"
                                                }`;
                                                return (
                                                    <tr key={land.id} className="hover:bg-gray-50 cursor-pointer">
                                                        <td className={className}>{land.rustic_land_value_id}</td>
                                                        <td className={className}>{land.rustic_land_desc}</td>
                                                        <td className={className}>
                                                            <NumericFormat
                                                                value={land.area.toFixed(4)}
                                                                displayType={'text'}
                                                                allowLeadingZeros
                                                                thousandSeparator=","
                                                            /> {getUnit}
                                                        </td>
                                                        <td className={className}>
                                                            <NumericFormat
                                                                value={land.value.toFixed(2)}
                                                                displayType={'text'}
                                                                prefix={'$'}
                                                                allowLeadingZeros
                                                                thousandSeparator=","
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                        </tbody>
                                        <tfoot>
                                        <tr className='border-t'>
                                            <td colSpan='2' className='py-3 px-5 text-right border-r'>
                                                <Typography
                                                    variant="small"
                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                >
                                                    Totales
                                                </Typography>
                                            </td>
                                            <td className='py-3 px-5 cursor-pointer bg-gray-50 border-b'>
                                                <NumericFormat
                                                    value={totalLandArea.toFixed(4)}
                                                    displayType={'text'}
                                                    allowLeadingZeros
                                                    thousandSeparator=","
                                                /> {getUnit}
                                            </td>
                                            <td className='py-3 px-5 cursor-pointer bg-gray-50 border-b border-r'>
                                                <NumericFormat
                                                    value={totalLandValue.toFixed(2)}
                                                    displayType={'text'}
                                                    prefix={'$'}
                                                    allowLeadingZeros
                                                    thousandSeparator=","
                                                />
                                            </td>
                                        </tr>
                                        </tfoot>
                                    </table>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>
            </div>

            {/* Construcciones */}
            <div className="flex-auto pb-2 pt-0">
                <h6 className="font-semibold uppercase text-gray-600 mt-8">
                    Construcciones
                </h6>
                <div className="flex flex-col gap-4">
                    <Card className='w-full border border-blue-gray-50' shadow={false}>
                        <CardBody>
                            <table className="w-full min-w-[640px] table-auto">
                                <thead>
                                <tr className='bg-gray-50'>
                                    {["Cve. Valuación", "Descripción", "Edad", "Sup. Construcción", "Valor"].map(
                                        (el) => (
                                            <th
                                                key={el}
                                                className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                            >
                                                <Typography
                                                    variant="small"
                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                >
                                                    {el}
                                                </Typography>
                                            </th>
                                        )
                                    )}
                                </tr>
                                </thead>
                                <tbody>
                                {(!constructions || constructions.length === 0) ? (
                                    <tr className="w-full">
                                        <td colSpan="100%" className="text-center py-3">
                                            <strong>El predio no cuenta con construcciones.</strong>
                                        </td>
                                    </tr>
                                ) : (constructions.map((construction, index) => {
                                        const className = `py-3 px-5 ${
                                            index === constructions.length - 1 ? "" : "border-b border-blue-gray-50"
                                        }`;
                                        return (
                                            <tr key={construction.id} className="hover:bg-gray-50 cursor-pointer">
                                                <td className={className}>{construction.construction_value_id}</td>
                                                <td className={className}>{construction.description}</td>
                                                <td className={className}>{construction.construction_devaluation_id}</td>
                                                <td className={className}>
                                                    <NumericFormat
                                                        value={construction.area.toFixed(2)}
                                                        displayType={'text'}
                                                        allowLeadingZeros
                                                        thousandSeparator=","
                                                    /> m<sup>2</sup>
                                                </td>
                                                <td className={className}>
                                                    <NumericFormat
                                                        value={construction.value.toFixed(2)}
                                                        displayType={'text'}
                                                        prefix={'$'}
                                                        allowLeadingZeros
                                                        thousandSeparator=","
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}

                                </tbody>
                                <tfoot>
                                <tr className='border-t'>
                                    <td colSpan='3' className='py-3 px-5 text-right border-r'>
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-medium uppercase text-blue-gray-400"
                                        >
                                            Totales
                                        </Typography>
                                    </td>
                                    <td className='py-3 px-5 cursor-pointer bg-gray-50 border-b'>
                                        <NumericFormat
                                            value={totalConstArea.toFixed(2)}
                                            displayType={'text'}
                                            allowLeadingZeros
                                            thousandSeparator=","
                                        /> m<sup>2</sup>
                                    </td>
                                    <td className='py-3 px-5 cursor-pointer bg-gray-50 border-b border-r'>
                                        <NumericFormat
                                            value={totalConstValue.toFixed(2)}
                                            displayType={'text'}
                                            prefix={'$'}
                                            allowLeadingZeros
                                            thousandSeparator=","
                                        />
                                    </td>
                                </tr>
                                </tfoot>
                            </table>
                            <div className='flex flex-row gap-4 w-full mt-6 text-center'>
                                <div className='flex flex-col w-full border rounded-md p-4 bg-gray-50'>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        Instalaciones especiales
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        <NumericFormat
                                            value={lot.special_facilities_value.toFixed(2)}
                                            displayType={'text'}
                                            prefix={'$'}
                                            allowLeadingZeros
                                            thousandSeparator=","
                                        />
                                    </Typography>
                                </div>
                                <div
                                    className={`flex flex-col w-full p-4 ${lot.special_facilities && 'border rounded-md bg-gray-50'}`}>
                                    <Typography
                                        variant="small"
                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                    >
                                        {lot.special_facilities && (
                                            <>
                                                Descripción completa del avaluo de las instalaciones especiales
                                                (Superficies,
                                                precios
                                                unitarios y valores).
                                            </>
                                        )}
                                    </Typography>
                                    <Typography
                                        variant="paragraph"
                                        className="font-medium"
                                    >
                                        {lot.special_facilities}
                                    </Typography>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            </div>

            {/* Avalúo */}
            <div className="flex-auto pb-2 pt-0">
                <h6 className="font-semibold uppercase text-gray-600 mt-8">
                    Avalúo
                </h6>
                <div className="flex flex-col lg:flex-row gap-4">
                    <Card className='w-full border border-blue-gray-50' shadow={false}>
                        <CardHeader className='bg-transparent' shadow={false}>
                            <Button className="flex items-center gap-3 float-right hover:bg-green-600"
                                    size='sm'
                                    color='green'
                                    onClick={() => {
                                        window.open(`lots/${parcelCode}/print_valuation.pdf`, '_blank');
                                    }}
                            >
                                <PrinterIcon className='h-4' />
                                Imprimir
                            </Button>
                        </CardHeader>
                        <CardBody>
                            <div className='mb-6'>
                                {lot.lot_location_type === 'URBANO' ? (
                                    (!lands || lands[0].corridor_value > 0) ? (
                                        <>
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                                            >
                                                Valores Corredor
                                            </Typography>
                                            <table className="w-full min-w-[640px] table-auto border">
                                                <thead>
                                                <tr className='bg-gray-50'>
                                                    {["Sup. Terreno", "Val. Corr. X m2", "Factor", "Valor"].map(
                                                        (el) => (
                                                            <th
                                                                key={el}
                                                                className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                                            >
                                                                <Typography
                                                                    variant="small"
                                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                                >
                                                                    {el}
                                                                </Typography>
                                                            </th>
                                                        )
                                                    )}
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {(!lands || lands.length === 0) ? (
                                                    <tr className="w-full">
                                                        <td colSpan="100%" className="text-center py-3">
                                                            <strong>El predio no cuenta con terrenos
                                                                registrados.</strong>
                                                        </td>
                                                    </tr>
                                                ) : (lands.map((land, index) => {
                                                        const className = `py-3 px-5 ${
                                                            index === land.length - 1 ? "" : "border-b border-blue-gray-50"
                                                        }`;
                                                        return (
                                                            <tr key={land.id}
                                                                className="hover:bg-gray-50 cursor-pointer">
                                                                <td className={className}>
                                                                    <NumericFormat
                                                                        value={land.area.toFixed(2)}
                                                                        displayType={'text'}
                                                                        allowLeadingZeros
                                                                        thousandSeparator=","
                                                                    /> {getUnit}
                                                                </td>
                                                                <td className={className}>
                                                                    <NumericFormat
                                                                        value={land.corridor_value.toFixed(2)}
                                                                        displayType={'text'}
                                                                        prefix={'$'}
                                                                        allowLeadingZeros
                                                                        thousandSeparator=","
                                                                    />
                                                                </td>
                                                                <td className={className}>{land.corridor_factor}</td>
                                                                <td className={className}>
                                                                    <NumericFormat
                                                                        value={land.valuation.toFixed(2)}
                                                                        displayType={'text'}
                                                                        prefix={'$'}
                                                                        allowLeadingZeros
                                                                        thousandSeparator=","
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                                </tbody>
                                            </table>
                                        </>
                                    ) : (
                                        <>
                                            <Typography
                                                variant="small"
                                                className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                                            >
                                                Terreno Urbano
                                            </Typography>
                                            <table className="w-full min-w-[640px] table-auto border">
                                                <thead>
                                                <tr className='bg-gray-50'>
                                                    {["Sup. Terreno", "Z. Homo.", "Val. X m2", "Factor", "Demerito", "Valor"].map(
                                                        (el) => (
                                                            <th
                                                                key={el}
                                                                className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                                            >
                                                                <Typography
                                                                    variant="small"
                                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                                >
                                                                    {el}
                                                                </Typography>
                                                            </th>
                                                        )
                                                    )}
                                                </tr>
                                                </thead>
                                                <tbody>
                                                {(!lands || lands.length === 0) ? (
                                                    <tr className="w-full">
                                                        <td colSpan="100%" className="text-center py-3">
                                                            <strong>El predio no cuenta con terrenos
                                                                registrados.</strong>
                                                        </td>
                                                    </tr>
                                                ) : (lands.map((land, index) => {
                                                        const className = `py-3 px-5 ${
                                                            index === land.length - 1 ? "" : "border-b border-blue-gray-50"
                                                        }`;
                                                        return (
                                                            <tr key={land.id}
                                                                className="hover:bg-gray-50 cursor-pointer">
                                                                <td className={className}>
                                                                    <NumericFormat
                                                                        value={land.area.toFixed(2)}
                                                                        displayType={'text'}
                                                                        allowLeadingZeros
                                                                        thousandSeparator=","
                                                                    /> {getUnit}
                                                                </td>
                                                                <td className={className}>{land.zone}</td>
                                                                <td className={className}>
                                                                    <NumericFormat
                                                                        value={land.land_value_per_m2.toFixed(2)}
                                                                        displayType={'text'}
                                                                        prefix={'$'}
                                                                        allowLeadingZeros
                                                                        thousandSeparator=","
                                                                    />
                                                                </td>
                                                                <td className={className}>{land.factor}</td>
                                                                <td className={className}>{land.devaluation_rate}</td>
                                                                <td className={className}>
                                                                    <NumericFormat
                                                                        value={land.valuation.toFixed(2)}
                                                                        displayType={'text'}
                                                                        prefix={'$'}
                                                                        allowLeadingZeros
                                                                        thousandSeparator=","
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                                </tbody>
                                            </table>
                                        </>
                                    )
                                ) : (
                                    <>
                                        <Typography
                                            variant="small"
                                            className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                                        >
                                            Terreno Rural
                                        </Typography>
                                        <table className="w-full min-w-[640px] table-auto border">
                                            <thead>
                                            <tr className='bg-gray-50'>
                                                {["Cve. Val.", "Descripción", "Superficie", "Val. x Ha", "Factor", "Valor"].map(
                                                    (el) => (
                                                        <th
                                                            key={el}
                                                            className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                                        >
                                                            <Typography
                                                                variant="small"
                                                                className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                            >
                                                                {el}
                                                            </Typography>
                                                        </th>
                                                    )
                                                )}
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {(!lands || lands.length === 0) ? (
                                                <tr className="w-full">
                                                    <td colSpan="100%" className="text-center py-3">
                                                        <strong>El predio no cuenta con terrenos registrados.</strong>
                                                    </td>
                                                </tr>
                                            ) : (lands.map((land, index) => {
                                                    const className = `py-3 px-5 ${
                                                        index === land.length - 1 ? "" : "border-b border-blue-gray-50"
                                                    }`;
                                                    return (
                                                        <tr key={land.id} className="hover:bg-gray-50 cursor-pointer">
                                                            <td className={className}>{land.rustic_land_value_id}</td>
                                                            <td className={className}>{land.rustic_land_desc}</td>
                                                            <td className={className}>
                                                                <NumericFormat
                                                                    value={land.area.toFixed(2)}
                                                                    displayType={'text'}
                                                                    allowLeadingZeros
                                                                    thousandSeparator=","
                                                                /> {getUnit}
                                                            </td>
                                                            <td className={className}>
                                                                <NumericFormat
                                                                    value={land.value_per_h2.toFixed(2)}
                                                                    displayType={'text'}
                                                                    prefix={'$'}
                                                                    allowLeadingZeros
                                                                    thousandSeparator=","
                                                                />
                                                            </td>
                                                            <td className={className}>{land.factor}</td>
                                                            <td className={className}>
                                                                <NumericFormat
                                                                    value={land.valuation.toFixed(2)}
                                                                    displayType={'text'}
                                                                    prefix={'$'}
                                                                    allowLeadingZeros
                                                                    thousandSeparator=","
                                                                />
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                            </tbody>
                                        </table>
                                    </>
                                )}
                            </div>
                            <hr className='my-6 border-0'/>
                            <div className='mb-6'>
                                <Typography
                                    variant="small"
                                    className="text-[11px] font-medium uppercase mb-2 text-blue-gray-400"
                                >
                                    Construcciones
                                </Typography>
                                <table className="w-full min-w-[640px] table-auto border">
                                    <thead>
                                    <tr className='bg-gray-50'>
                                        {["Cve. Valuación", "Descripción", "Sup. Construcción", "Val. Tablas", "Edad", "Fac. Edad", "Valor"].map(
                                            (el) => (
                                                <th
                                                    key={el}
                                                    className="border-b border-blue-gray-50 py-3 px-6 text-left"
                                                >
                                                    <Typography
                                                        variant="small"
                                                        className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                    >
                                                        {el}
                                                    </Typography>
                                                </th>
                                            )
                                        )}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {(!constructions || constructions.length === 0) ? (
                                        <tr className="w-full">
                                            <td colSpan="100%" className="text-center py-3">
                                                <strong>El predio no cuenta con construcciones.</strong>
                                            </td>
                                        </tr>
                                    ) : (constructions.map((construction, index) => {
                                            const className = `py-3 px-5 ${
                                                index === constructions.length - 1 ? "" : "border-b border-blue-gray-50"
                                            }`;
                                            return (
                                                <tr key={construction.id} className="hover:bg-gray-50 cursor-pointer">
                                                    <td className={className}>{construction.construction_value_id}</td>
                                                    <td className={className}>{construction.description}</td>
                                                    <td className={className}>
                                                        <NumericFormat
                                                            value={construction.area.toFixed(2)}
                                                            displayType={'text'}
                                                            allowLeadingZeros
                                                            thousandSeparator=","
                                                        /> m<sup>2</sup>
                                                    </td>
                                                    <td className={className}>
                                                        <NumericFormat
                                                            value={construction.value_per_m2.toFixed(2)}
                                                            displayType={'text'}
                                                            prefix={'$'}
                                                            allowLeadingZeros
                                                            thousandSeparator=","
                                                        />
                                                    </td>
                                                    <td className={className}>{construction.construction_devaluation_id}</td>
                                                    <td className={className}>{construction.devaluation_rate}</td>
                                                    <td className={className}>
                                                        <NumericFormat
                                                            value={construction.valuation.toFixed(2)}
                                                            displayType={'text'}
                                                            prefix={'$'}
                                                            allowLeadingZeros
                                                            thousandSeparator=","
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                    </tbody>
                                </table>
                            </div>
                            <hr className='my-6 border-0'/>
                            <div className="flex grid-cols-3 w-full mt-6 gap-4">
                                <Card className='w-full border rounded-md bg-gray-50' shadow={false}>
                                    <CardHeader shadow={false} className='my-2 bg-transparent'>
                                        <Typography
                                            variant="small"
                                            color="gray"
                                            className="font-medium text-center"
                                        >
                                            Totales Terreno
                                        </Typography>
                                    </CardHeader>
                                    <CardBody className='pt-4'>
                                        <div className='flex flex-row gap-4 w-full text-center'>
                                            <div className='flex flex-col w-full'>
                                                <Typography
                                                    variant="small"
                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                >
                                                    Sup. Terreno
                                                </Typography>
                                                <Typography
                                                    variant="paragraph"
                                                    className="font-medium"
                                                >
                                                    <NumericFormat
                                                        value={lot.land_area.toFixed(2)}
                                                        displayType={'text'}
                                                        allowLeadingZeros
                                                        thousandSeparator=","
                                                    /> {getUnit}
                                                </Typography>
                                            </div>
                                            <div className='flex flex-col w-full'>
                                                <Typography
                                                    variant="small"
                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                >
                                                    Val. Terreno
                                                </Typography>
                                                <Typography
                                                    variant="paragraph"
                                                    className="font-medium"
                                                >
                                                    <NumericFormat
                                                        value={totalLandValuation.toFixed(2)}
                                                        displayType={'text'}
                                                        prefix={'$'}
                                                        allowLeadingZeros
                                                        thousandSeparator=","
                                                    />
                                                </Typography>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                                <Card className='w-full border rounded-md bg-gray-50' shadow={false}>
                                    <CardHeader shadow={false} className='my-2 bg-transparent'>
                                        <Typography
                                            variant="small"
                                            color="gray"
                                            className="font-medium text-center"
                                        >
                                            Totales Construcción
                                        </Typography>
                                    </CardHeader>
                                    <CardBody className='pt-4'>
                                        <div className='flex flex-row gap-4 w-full text-center'>
                                            <div className='flex flex-col w-full'>
                                                <Typography
                                                    variant="small"
                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                >
                                                    Sup. Construcción
                                                </Typography>
                                                <Typography
                                                    variant="paragraph"
                                                    className="font-medium"
                                                >
                                                    <NumericFormat
                                                        value={totalConstArea.toFixed(2)}
                                                        displayType={'text'}
                                                        allowLeadingZeros
                                                        thousandSeparator=","
                                                    /> m<sup>2</sup>
                                                </Typography>
                                            </div>
                                            <div className='flex flex-col w-full'>
                                                <Typography
                                                    variant="small"
                                                    className="text-[11px] font-medium uppercase text-blue-gray-400"
                                                >
                                                    Val. Construcción
                                                </Typography>
                                                <Typography
                                                    variant="paragraph"
                                                    className="font-medium"
                                                >
                                                    <NumericFormat
                                                        value={totalConstValuation.toFixed(2)}
                                                        displayType={'text'}
                                                        prefix={'$'}
                                                        allowLeadingZeros
                                                        thousandSeparator=","
                                                    />
                                                </Typography>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </div>
                        </CardBody>
                    </Card>
                </div>
            </div>
        </>
    )
};

export default LotDetails;
